import { clamp, smoothstep, valueNoise } from '@/core/rng';

/**
 * The sky's moods, as a pure field.
 *
 * Weather is the one thing that changes the whole rendering equation at once
 * — light, fog, colour, motion — and the first honest version of it needs no
 * simulation at all: fronts are broad, slow, deterministic features drifting
 * on the island's one wind, a pure function of (seed, time, place) exactly
 * like the terrain is of (seed, place). Nothing writes; two sessions at the
 * same world-second see the same sky over the same valley.
 *
 * It lives in `world/`, not `render/`, on purpose: rain wetting fuel and
 * lightning choosing a storm to ride are simulation questions, and when the
 * sim takes them up it must read the SAME field the eye sees — a fire lit by
 * a storm nobody can see would break the theory a player is building. This
 * module imports nothing but the rng core, so `src/sim/` may import it
 * without touching law 7.
 */
export interface Weather {
  /** 0 clear sky … 1 full grey lid. */
  overcast: number;
  /** 0 dry … 1 hard rain. Rain arrives only under most of an overcast. */
  rain: number;
  /** Valley fog, 0..1 — a morning thing, burnt off by midday. */
  fog: number;
  /**
   * 0 calm … 1 a violent convective cell — the part of a front that thunders.
   * Storms live in the very top of the same front field that rains, so a
   * storm is always inside weather anyone can see coming, and they swell with
   * the season (stormSeason). The sim's lightning rides exactly this number;
   * the renderer is free to darken the lid's core and flash where it is high,
   * and both will always be talking about the same sky.
   */
  storm: number;
}

/** The island's one wind — must agree with main.ts's WIND. */
const WIND_X = 5.2, WIND_Z = 1.6;
const DAY = 86400;
/** World-seconds in one year. The sky is the first thing to have a season. */
export const YEAR = 365 * DAY;

/**
 * One annual swell, 0..1: a raised-cosine with its trough at `trough` (as a
 * fraction of the year) and its peak half a year later. The two seasonal
 * curves below are this shape at different phases, so they stay in a fixed,
 * legible relation to each other instead of drifting as separate tunings.
 */
const annualSwell = (t: number, trough: number): number => {
  const phase = (((t / YEAR) % 1) + 1) % 1;
  return 0.5 - 0.5 * Math.cos((phase - trough) * 2 * Math.PI);
};

/**
 * How charged the sky is, 0..1 — convective energy builds through the warm
 * half of the year and peaks in late summer (phase ~0.55). Storms never
 * vanish entirely; the 0.25 floor keeps the odd out-of-season thunderhead.
 */
export const stormSeason = (t: number): number => 0.25 + 0.75 * annualSwell(t, 0.05);

/**
 * How cured the land's dead fuel is, 0..1 — peaks at phase ~0.65, a little
 * after the storms do, which is what makes a late-summer bolt so much more
 * consequential than a spring one. Fire reads this; a renderer may one day
 * straw-tint late-summer grass with the same number, and then the land will
 * look exactly as flammable as it is.
 */
export const seasonalDryness = (t: number): number => annualSwell(t, 0.15);

export function weatherAt(seed: number, t: number, x: number, z: number): Weather {
  // Fronts: ~14 km features drifting downwind at about 3 m/s — one crosses
  // the island in an afternoon — evolving in place over ~9 hours. Two
  // octaves, so a front has structure without flickering.
  const fx = (x - t * WIND_X * 0.55) / 14000;
  const fz = (z - t * WIND_Z * 0.55) / 14000;
  const slow = t / 32000;
  const front = valueNoise(fx + slow, fz - slow * 0.6, seed + 0x77aa) * 0.65
              + valueNoise(fx * 2.3 + 7.7, fz * 2.3 + slow * 0.5, seed + 0x77ab) * 0.35;
  const overcast = smoothstep(0.42, 0.66, front);
  const rain = smoothstep(0.60, 0.80, front);
  // The storm is the front's violent core: it begins where rain is already
  // committed (front 0.68 → rain ≈ 0.35), so a thundering sky is always a
  // visibly wet one — but its margins reach ground the hard rain has not,
  // which is where a bolt finds anything worth igniting.
  const storm = smoothstep(0.68, 0.92, front) * stormSeason(t);

  // Fog is its own slower, stiller field, gated hard to the morning hours —
  // it pools where it pools, then the day burns it away.
  const tod = ((t % DAY) + DAY) % DAY / DAY;
  const morning = smoothstep(0.14, 0.23, tod) * (1 - smoothstep(0.32, 0.44, tod));
  const fogField = valueNoise(x / 9000 + slow * 0.4, z / 9000, seed + 0x77ac);
  const fog = morning * smoothstep(0.48, 0.72, fogField);

  return {
    overcast: clamp(overcast, 0, 1),
    rain: clamp(rain, 0, 1),
    fog: clamp(fog, 0, 1),
    storm: clamp(storm, 0, 1),
  };
}
